import React, { useContext, useEffect, useState } from "react";
import { logInWithGoogle, resetPassword, handlePasswordReset } from "../../utils/loginModule";
import { getAuth, sendEmailVerification, applyActionCode } from "firebase/auth";
import Title from "../general/title";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  signInWithEmailAndPassword,
} from "firebase/auth";
import { faAlignLeft, faArrowPointer, faFilm, faHeadphonesSimple, faHighlighter, faScissors } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faThumbsUp } from "@fortawesome/free-regular-svg-icons";
import GlobalStateContext from "../general/globalState";
import getTranslationString from "../general/translateToString";
import Translate from "../general/translate";
import posthog from "posthog-js";

const LoginForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isRecoverPassword, setIsRecoverPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const auth = getAuth();
  const [user, loading, error] = useAuthState(auth);

  const { language } = useContext(GlobalStateContext);

  const [placeholders, setPlaceholders] = useState({
    emailPlaceholder: "",
    passwordPlaceholder: "",
  });

  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [showVerifyButton, setShowVerifyButton] = useState(false);

  const [verificationUser, setVerificationUser] = useState(null);

  const [isVerificationMode, setIsVerificationMode] = useState(false);

  const [isProcessingVerification, setIsProcessingVerification] = useState(false);

  const [newPassword, setNewPassword] = useState("");
  const [isResetMode, setIsResetMode] = useState(false);

  useEffect(() => {
    const loadPlaceholders = async () => {
      const emailPlaceholder = getTranslationString("placeholder.email", "login", "loginForm", language);
      const passwordPlaceholder = getTranslationString("placeholder.password", "login", "loginForm", language);
      
      setPlaceholders({
        emailPlaceholder,
        passwordPlaceholder,
      });
    };

    loadPlaceholders();
  }, [language]);

  useEffect(() => {
    const handleVerification = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const mode = urlParams.get('mode');
      const oobCode = urlParams.get('oobCode');
      
      if (mode === 'resetPassword' && oobCode) {
        setIsResetMode(true);
      }
      
      if (mode === 'verifyEmail' && oobCode && !isProcessingVerification) {
        setIsProcessingVerification(true);
        try {
          await applyActionCode(auth, oobCode);
          const currentUser = auth.currentUser;
          if (currentUser) {
            await currentUser.getIdToken(true);
            await currentUser.reload();
            
            window.location.href = "/squeezed";
          } else {
            const email = urlParams.get('email');
            if (email) {
              setEmail(email);
              setIsVerificationMode(false);
            }
          }
        } catch (error) {
          console.error('Verification error:', error);
          setErrorMessage(getTranslationString("error.verificationFailed", "login", "loginForm", language));
        }
      }
    };
    handleVerification();
  }, []); 

  useEffect(() => {
    if (user) {
      if (!user.emailVerified && user.metadata.createdAt > 1726272000000) {
        const currentUser = user;
        auth.signOut();
        setIsVerificationMode(true);
        setVerificationUser(currentUser);
        return;
      }
      window.location.href = "/squeezed";
    }
  }, [user, loading]);

  const logInWithEmailAndPassword = async (event, loginEmail, loginPassword) => {
    event.preventDefault(); 
    try {
      const user = await signInWithEmailAndPassword(
        auth,
        loginEmail,
        loginPassword
      );
      
      if (user && user.user) {
        posthog.identify(user.user.uid);
        
        const userProperties = {
          email: user.user.email,
          authProvider: 'email',
          lastLoginAt: new Date().toISOString()
        };
        posthog.people.set(userProperties);
      }
      
    } catch (error) {
      if (error.code === "auth/wrong-password"){
        const message = getTranslationString("error.incorrectPassword", "login", "loginForm", language)
        setErrorMessage(message);
        setShowCreateAccount(false);
        return;
      }
      if (error.code === "auth/user-not-found" || error.code === "auth/invalid-email"){
        const message = getTranslationString("error.userNotFound", "login", "loginForm", language)
        setErrorMessage(message);
        setShowCreateAccount(true);
        return;
      }
      console.log(error.code);
      const message = getTranslationString("error.somethingWentWrong", "login", "loginForm", language)
      setErrorMessage(message);
      setShowCreateAccount(false);
    }
  };

  const sendVerificationEmail = async () => {
    try {

      if (verificationUser) {
        await sendEmailVerification(verificationUser);
        const message = getTranslationString("success.verificationEmailSent", "login", "loginForm", language);
        setErrorMessage(message);
        setShowVerifyButton(false);
      }
    } catch (error) {
      const message = getTranslationString("error.verificationEmailFailed", "login", "loginForm", language);
      setErrorMessage(message);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    const urlParams = new URLSearchParams(window.location.search);
    const oobCode = urlParams.get('oobCode');
    
    if (!oobCode) {
      setErrorMessage('Invalid reset link');
      return;
    }

    try {
      const result = await handlePasswordReset(oobCode, newPassword);
      if (result.success) {
        setSuccessMessage('Password successfully reset. Please log in with your new password.');
        setIsResetMode(false);
      } else {
        setErrorMessage('Failed to reset password. Please try again.');
      }
    } catch (error) {
      setErrorMessage('An error occurred while resetting your password.');
    }
  };

  const getLoginButton = () => {
    if(isRecoverPassword){
      return(
        <button 
          className="bg-[#FFCA1D] text-[#483187] mb-[40px] w-full lg:w-3/4 h-12 font-bold text-[18px] px-5 rounded shadow hover:shadow-lg outline-none focus:outline-none" 
          onClick={(e) => {resetPassword(e, email)}}>
            <Translate id="button.sendResetEmail" foldername="login" filename="loginForm" />
        </button>
      )
    }

    return(
      <button 
        className="bg-[#FFCA1D] text-[#483187] mb-[40px] w-full lg:w-3/4 h-12 font-bold text-[18px] px-5 rounded shadow hover:shadow-lg outline-none focus:outline-none" 
        onClick={(event) => {logInWithEmailAndPassword(event, email, password)}}>
          <Translate id="button.login" foldername="login" filename="loginForm" />
      </button>
    )
  }

  if (isResetMode) {
    return (
      <div className="w-5/6 md:w-full max-w-7xl mx-auto flex flex-col mb-24 mt-14 items-center justify-center">
        <form className="w-full md:w-1/3 flex flex-col items-center">
          <Title 
            title="Reset Your Password"
            classes="text-2xl text-[#FFCA1D] mb-4"
          />
          <div className="mb-6 w-full lg:w-3/4">
            <input
              type="password"
              className="form-control w-full px-4 py-2 text-md font-normal text-gray-700 bg-white bg-clip-padding border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-blue-600 focus:outline-none"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
            />
          </div>
          <button 
            className="bg-[#FFCA1D] text-[#483187] mb-[40px] w-full lg:w-3/4 h-12 font-bold text-[18px] px-5 rounded shadow hover:shadow-lg outline-none focus:outline-none"
            onClick={handleResetSubmit}
          >
            Set New Password
          </button>
          {successMessage && (
            <div className="text-[#green] mb-4">{successMessage}</div>
          )}
          {errorMessage && (
            <div className="text-[#ee4747] mb-4">{errorMessage}</div>
          )}
        </form>
      </div>
    );
  }

  return (
    <div className="w-5/6 md:w-full max-w-7xl mx-auto flex flex-col lg:flex-row mb-24 mt-14 items-center justify-center">         
         <div className="w-full md:w-1/3 flex flex-col items-center text-left mb-10 lg:mb-0">
            {isVerificationMode ? (
              <div className="w-full lg:w-3/4 text-center bg-[#4B22A1] bg-opacity-30 p-6 rounded-lg">
                <Title 
                  title={<Translate id="text.pleaseVerifyAccount" foldername="login" filename="loginForm" />}
                  classes="text-2xl text-[#FFCA1D] mb-4"
                />
                <p className="text-[#fafafa] mb-4">
                  <Translate id="text.checkSpamFolder" foldername="login" filename="loginForm" />
                </p>
                <button 
                  className="bg-[#FFCA1D] text-[#483187] w-full h-12 font-bold text-[18px] px-5 rounded shadow hover:shadow-lg outline-none focus:outline-none"
                  onClick={sendVerificationEmail}
                >
                  <Translate id="button.resendVerificationEmail" foldername="login" filename="loginForm" />
                </button>
                {errorMessage && (
                  <p className="mt-4 text-[#fafafa]">{errorMessage}</p>
                )}
              </div>
            ) : (
              <form className="w-full flex flex-col items-center">
                <div className="mb-6 w-full lg:w-3/4">
                  <input
                    type="text"
                    className="form-control block w-full px-4 py-2 text-md font-normal text-gray-700 bg-white bg-clip-padding border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-blue-600 focus:outline-none"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={placeholders.emailPlaceholder}
                  />
                </div>

                <div className={`mb-6 w-full lg:w-3/4 ${isRecoverPassword ? "hidden" : "block"}`}>
                  <input
                    type="password"
                    className="form-control w-full px-4 py-2 text-md font-normal text-gray-700 bg-white bg-clip-padding border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-blue-600 focus:outline-none"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={placeholders.passwordPlaceholder}
                  />
                </div>
                
                {getLoginButton()}
                <div className={errorMessage ? "flex flex-col  justify-between  w-full lg:w-3/4 mb-4": "hidden"}>
                <Title title={errorMessage} classes={"text-left text-[#ee4747] pb-2"} />
                  {showVerifyButton && (
                    <div className="" onClick={sendVerificationEmail}>
                      <Title 
                        title={<Translate id="button.sendVerificationEmail" foldername="login" filename="loginForm" />} 
                        classes="text-md text-[#fafafa] cursor-pointer" 
                      />  
                    </div>
                  )}
                   
                  {showCreateAccount && (
                   <div className="flex items-center justify-between w-full lg:w-3/4 mb-4" onClick={() => location.href="/register"}>
                    <Title title={<Translate id="link.createAccount" foldername="login" filename="loginForm" />} classes="text-md text-[#fafafa] cursor-pointer" />  
                  </div>
                  )}
                </div>
                <div className="flex items-center justify-between w-full lg:w-3/4" onClick={() => setIsRecoverPassword(true)}>
                  <Title 
                    title={<Translate id="link.forgotPassword" foldername="login" filename="loginForm" />} 
                    classes="text-md text-[#fafafa] cursor-pointer" 
                  />  
                </div>

                <div className="flex items-center justify-between w-full lg:w-3/4 mt-5" onClick={() => location.href="/register"}>
                  <Title title={<Translate id="link.createAccount" foldername="login" filename="loginForm" />} classes="text-md text-[#fafafa] cursor-pointer" />  
                </div>

                <div
                  className="w-full lg:w-3/4 flex items-center my-4 before:flex-1 before:border-t before:border-gray-300 before:mt-0.5 after:flex-1 after:border-t after:border-gray-300 after:mt-0.5"
                >
                  <p className="text-center font-semibold mx-4 mb-0 text-[#fafafa]"><Translate id="text.or" foldername="login" filename="loginForm" /></p>
                </div>
                <a
                  className="h-12 px-7 py-3 text-[#2b2b2b] bg-[#fff] font-bold text-sm leading-snug uppercase rounded shadow-md hover:shadow-lg focus:shadow-lg focus:outline-none focus:ring-0 active:shadow-lg transition duration-150 ease-in-out w-full lg:w-3/4 flex justify-center items-center mb-3"
                  onClick={() => logInWithGoogle(false)}
                  role="button"
                  data-mdb-ripple="true"
                  data-mdb-ripple-color="light"
                >
                  <svg viewBox="0 0 533.5 544.3" width="20" height="20" className="w-3.5 h-3.5 mr-2"><path fill="currentColor" d="M533.5 278.4c0-18.5-1.5-37.1-4.7-55.3H272.1v104.8h147c-6.1 33.8-25.7 63.7-54.4 82.7v68h87.7c51.5-47.4 81.1-117.4 81.1-200.2z"></path><path fill="currentColor" className="google_svg__st1" d="M272.1 544.3c73.4 0 135.3-24.1 180.4-65.7l-87.7-68c-24.4 16.6-55.9 26-92.6 26-71 0-131.2-47.9-152.8-112.3H28.9v70.1c46.2 91.9 140.3 149.9 243.2 149.9z"></path><path fill="currentColor" d="M119.3 324.3c-11.4-33.8-11.4-70.4 0-104.2V150H28.9c-38.6 76.9-38.6 167.5 0 244.4l90.4-70.1z"></path><path fill="currentColor" d="M272.1 107.7c38.8-.6 76.3 14 104.4 40.8l77.7-77.7C405 24.6 339.7-.8 272.1 0 169.2 0 75.1 58 28.9 150l90.4 70.1c21.5-64.5 81.8-112.4 152.8-112.4z"></path></svg><Translate id="button.continueWithGoogle" foldername="login" filename="loginForm" />
                </a>
              </form>
            )}
          </div>
         
          <div className="w-full lg:w-1/2 flex flex-col items-center text-left lg:pl-10 hidden sm:block">
            <Title title={<Translate id="text.podcastDescription" foldername="login" filename="loginForm" />} classes={"text-3xl leading-tight font-[700] text-[#fafafa] mb-[40px]"} />
                      
            <div className="flex flex-wrap justify-left gap-4 mb-12 max-w-[1100px] m-auto text-xl md:text-xl font-bold">
              <div className="flex items-center py-2 px-4 bg-[#4B22A1] bg-opacity-30 rounded-lg shadow space-x-2">
                <FontAwesomeIcon icon={faAlignLeft} className="text-[#F39A7B]"/>
                <span className="text-white text-opacity-90"><Translate id="feature.transcripts" foldername="login" filename="loginForm" /></span>
              </div>
              <div className="flex items-center py-2 px-4 bg-[#4B22A1] bg-opacity-30 rounded-lg shadow space-x-2">
                <FontAwesomeIcon icon={faHighlighter} className="text-[#3ADAD9]" />
                <span className="text-white text-opacity-90"><Translate id="feature.showNotes" foldername="login" filename="loginForm" /></span>
              </div>
              <div className="flex items-center py-2 px-4 bg-[#4B22A1] bg-opacity-30 rounded-lg shadow space-x-2">
                <FontAwesomeIcon icon={faPaperPlane} className="text-[#FFCA1D]"/>
                <span className="text-white text-opacity-90"><Translate id="feature.newslettersAndBlogs" foldername="login" filename="loginForm" /></span>
              </div>
              <div className="flex items-center py-2 px-4 bg-[#4B22A1] bg-opacity-30 rounded-lg shadow space-x-2">
                <FontAwesomeIcon icon={faThumbsUp} className="text-[#4A81EC]"/>
                <span className="text-white text-opacity-90"><Translate id="feature.socialPosts" foldername="login" filename="loginForm" /></span>
              </div>
              <div className="flex items-center py-2 px-4 bg-[#4B22A1] bg-opacity-30 rounded-lg shadow space-x-2">
                <FontAwesomeIcon icon={faFilm} className="text-[#FFCA1D]"/>
                <span className="text-white text-opacity-90"><Translate id="feature.shortClipsAndAudiograms" foldername="login" filename="loginForm" /></span>
              </div>
              <div className="flex items-center py-2 px-4 bg-[#4B22A1] bg-opacity-30 rounded-lg shadow space-x-2">
                <FontAwesomeIcon icon={faArrowPointer} className="text-[#F39A7B]"/>
                <span className="text-white text-opacity-90"><Translate id="feature.podcastWebsite" foldername="login" filename="loginForm" /></span>
              </div>
              <div className="flex items-center py-2 px-4 bg-[#4B22A1] bg-opacity-30 rounded-lg shadow space-x-2">
                <FontAwesomeIcon icon={faHeadphonesSimple} className="text-[#3ADAD9]"/>
                <span className="text-white text-opacity-90"><Translate id="feature.aiAudioEnhancement" foldername="login" filename="loginForm" /></span>
              </div>
              <div className="flex items-center py-2 px-4 bg-[#4B22A1] bg-opacity-30 rounded-lg shadow space-x-2">
                <FontAwesomeIcon icon={faScissors} className="text-[#4A81EC]"/>
                <span className="text-white text-opacity-90"><Translate id="feature.removeSilences" foldername="login" filename="loginForm" /></span>
              </div>
            </div>
          </div>
        
    </div>
  );
}

export default LoginForm;